import { imageWithinLimits, sniffInboundMedia } from './media-sniff.js';

/**
 * A campaign form's own `file` field — the bank receipt, the beneficiary
 * letter, the proof of residence an organization's form asks a donor for.
 *
 * The cap is the 7 MiB an inbound photograph may be, because that is what a
 * phone produces and what the decoder bounds in `INBOUND_MEDIA_LIMITS` were
 * chosen for; a scanned document is smaller. A campaign's form may ask for
 * less with the field's own `maxSizeBytes`, never for more.
 */
export const FORM_FILE_MAX_BYTES = 7 * 1024 * 1024;

export type FormFileContentType = 'image/jpeg' | 'image/png' | 'application/pdf';

export type FormFileExtension = 'jpg' | 'png' | 'pdf';

export type SniffedFormFile =
  | { kind: 'file'; contentType: FormFileContentType; extension: FormFileExtension }
  | { kind: 'unsupported' }
  | { kind: 'damaged' }
  | { kind: 'oversized' };

const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46, 0x2d] as const; // "%PDF-"

/** How far back from the end `%%EOF` is looked for before a PDF is judged damaged. */
const PDF_TRAILER_WINDOW = 2048;

const PDF_TRAILER = [0x25, 0x25, 0x45, 0x4f, 0x46] as const; // "%%EOF"

function startsWith(bytes: Uint8Array, prefix: readonly number[]): boolean {
  return prefix.every((byte, index) => bytes[index] === byte);
}

function hasTrailer(bytes: Uint8Array): boolean {
  const from = Math.max(0, bytes.length - PDF_TRAILER_WINDOW);
  for (let at = bytes.length - PDF_TRAILER.length; at >= from; at -= 1) {
    if (PDF_TRAILER.every((byte, index) => bytes[at + index] === byte)) return true;
  }
  return false;
}

/**
 * The bytes decide, never the browser's content type and never the extension —
 * .claude/rules/file-uploads.md.
 *
 * Three types are admitted, and they are the three a document a donor is asked
 * for actually arrives as: a JPEG, a PNG and a PDF. An image is held to the
 * same header bounds the inbound-photograph reader applies, because the risk is
 * the same decoder. A PDF must say `%PDF-` and carry an `%%EOF` near its end;
 * nothing here parses it further, and nothing in the system ever renders it —
 * it is stored, scanned and handed back as a download.
 *
 * Everything else — an Office document, a ZIP, an SVG, a HEIF, a video — is
 * `unsupported`. A header that lies about itself is `damaged`.
 */
export function sniffFormFile(bytes: Uint8Array): SniffedFormFile {
  if (startsWith(bytes, PDF_SIGNATURE)) {
    const major = bytes[PDF_SIGNATURE.length];
    const usable = major === 0x31 || major === 0x32; // "1" or "2"
    if (!usable || !hasTrailer(bytes)) return { kind: 'damaged' };
    return { kind: 'file', contentType: 'application/pdf', extension: 'pdf' };
  }

  const sniffed = sniffInboundMedia(bytes);
  if (sniffed.kind === 'damaged') return { kind: 'damaged' };
  if (sniffed.kind !== 'image') return { kind: 'unsupported' };
  if (!imageWithinLimits(sniffed)) return { kind: 'oversized' };
  return { kind: 'file', contentType: sniffed.contentType, extension: sniffed.extension };
}

/**
 * Whether a sniffed type satisfies the field's own `accept` list. An empty or
 * absent list accepts every type this reader admits at all — `accept` narrows
 * what the form asks for and can never widen it.
 *
 * The three spellings a form builder writes are honoured: an exact media type
 * (`application/pdf`), a wildcard (`image/*`) and an extension (`.pdf`). An
 * entry in none of those shapes matches nothing, so a typo in the schema
 * refuses the upload rather than quietly accepting everything.
 */
export function formFileAccepted(
  sniffed: { contentType: FormFileContentType; extension: FormFileExtension },
  accept: readonly string[] | null | undefined,
): boolean {
  if (!accept || accept.length === 0) return true;
  return accept.some((raw) => {
    const entry = raw.trim().toLowerCase();
    if (entry === '') return false;
    if (entry.startsWith('.')) {
      const named = entry.slice(1);
      return named === sniffed.extension || (named === 'jpeg' && sniffed.extension === 'jpg');
    }
    if (entry.endsWith('/*')) return sniffed.contentType.startsWith(entry.slice(0, -1));
    return entry === sniffed.contentType;
  });
}

/**
 * The bound one field imposes, which is its own `maxSizeBytes` held under the
 * kind's cap. A schema asking for more than the kind allows gets the kind's
 * cap, because the cap is what the reader and the scanner were sized for.
 */
export function formFileMaxBytes(rules: { maxSizeBytes?: number | null | undefined }): number {
  const asked = rules.maxSizeBytes;
  if (asked == null || asked <= 0) return FORM_FILE_MAX_BYTES;
  return Math.min(asked, FORM_FILE_MAX_BYTES);
}
